import { RECURRENCE_FREQUENCIES, WEEKDAYS } from './appConstants';

// Convert values from forms or APIs into the exact recurrence payload shape.
// Monthly repeat values remain numbers; weekly and yearly values are strings.
export const normalizeRecurrenceConfig = (config) => {
  if (!config?.frequency || config.repeats_on === undefined || config.repeats_on === null || config.repeats_on === '') {
    return null;
  }

  const frequency = String(config.frequency).toUpperCase();
  return {
    frequency,
    repeats_on: frequency === RECURRENCE_FREQUENCIES.MONTHLY
      ? Number(config.repeats_on)
      : String(config.repeats_on).toUpperCase()
  };
};

export const recurrenceConfigsEqual = (left, right) => {
  const normalizedLeft = normalizeRecurrenceConfig(left);
  const normalizedRight = normalizeRecurrenceConfig(right);
  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
};

export const isRecurrenceComplete = (isRecurring, config) => (
  !isRecurring || Boolean(normalizeRecurrenceConfig(config))
);

// GET APIs can return next_rep_on as DD-MM-YYYY, while recurrence_config uses
// YYYY-MM-DD for yearly dates. Parse both explicitly to avoid browser ambiguity.
const parseDate = (value) => {
  if (!value) return null;
  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [day, month, year] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatRecurrenceDate = (value) => {
  const date = value instanceof Date ? value : parseDate(value);
  if (!date || Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatRecurrenceMonthDay = (value) => {
  const date = value instanceof Date ? value : parseDate(value);
  if (!date || Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

export const formatOrdinal = (value) => {
  const day = Number(value);
  if (!day) return '';
  const remainder100 = day % 100;
  const suffix = remainder100 >= 11 && remainder100 <= 13
    ? 'th'
    : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
  return `${day}${suffix}`;
};

// Calculate the next strictly-future occurrence for create/update previews.
// Read-only ticket views use the backend-derived next_rep_on value instead.
export const calculateNextRecurrence = (config, from = new Date()) => {
  const normalized = normalizeRecurrenceConfig(config);
  if (!normalized) return null;

  const today = new Date(from.getFullYear(), from.getMonth(), from.getDate());

  if (normalized.frequency === RECURRENCE_FREQUENCIES.WEEKLY) {
    const targetDay = WEEKDAYS.indexOf(normalized.repeats_on);
    if (targetDay < 0) return null;
    let daysAhead = (targetDay - today.getDay() + 7) % 7;
    if (daysAhead === 0) daysAhead = 7;
    const next = new Date(today);
    next.setDate(today.getDate() + daysAhead);
    return next;
  }

  if (normalized.frequency === RECURRENCE_FREQUENCIES.MONTHLY) {
    const targetDate = Number(normalized.repeats_on);
    if (targetDate < 1 || targetDate > 31) return null;
    // Skip calendar months that do not contain the requested day (for example, the 31st).
    for (let offset = 0; offset < 24; offset += 1) {
      const year = today.getFullYear() + Math.floor((today.getMonth() + offset) / 12);
      const month = (today.getMonth() + offset) % 12;
      const candidate = new Date(year, month, targetDate);
      if (candidate.getMonth() === month && candidate > today) return candidate;
    }
    return null;
  }

  if (normalized.frequency === RECURRENCE_FREQUENCIES.YEARLY) {
    const selectedDate = parseDate(normalized.repeats_on);
    if (!selectedDate) return null;
    if (selectedDate > today) return selectedDate;

    // Once the selected date has passed (including today), retain its month/day
    // and find the next valid year. The validity check handles leap-day recurrence.
    const targetMonth = selectedDate.getMonth();
    const targetDay = selectedDate.getDate();
    for (let yearOffset = 0; yearOffset < 8; yearOffset += 1) {
      const candidate = new Date(today.getFullYear() + yearOffset, targetMonth, targetDay);
      const isValidCalendarDate = candidate.getMonth() === targetMonth && candidate.getDate() === targetDay;
      if (isValidCalendarDate && candidate > today) return candidate;
    }
    return null;
  }

  return null;
};

// Build localized recurrence wording for editable and read-only ticket views.
export const getRecurrenceSummary = (config, t, readOnly = false) => {
  const normalized = normalizeRecurrenceConfig(config);
  if (!normalized) return t('taskModal.recurrence.summary.incomplete');

  const mode = readOnly ? 'readOnly' : 'editable';

  if (normalized.frequency === RECURRENCE_FREQUENCIES.WEEKLY) {
    return t(`taskModal.recurrence.summary.${mode}.weekly`, {
      day: t(`taskModal.recurrence.weekdays.${normalized.repeats_on.toLowerCase()}`)
    });
  }
  if (normalized.frequency === RECURRENCE_FREQUENCIES.MONTHLY) {
    return t(`taskModal.recurrence.summary.${mode}.monthly`, {
      day: formatOrdinal(normalized.repeats_on)
    });
  }
  return t(`taskModal.recurrence.summary.${mode}.yearly`, {
    date: formatRecurrenceMonthDay(normalized.repeats_on)
  });
};
